<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import Textarea from 'dashboard/components-next/textarea/TextArea.vue';
import StudioSection from '../components/StudioSection.vue';
import StudioBadge from '../components/StudioBadge.vue';
import StudioEmptyState from '../components/StudioEmptyState.vue';

const props = defineProps({
  bot: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const records = computed(
  () => store.getters['aiBotTransferCases/getRecords'](props.bot.id) || []
);
const uiFlags = useMapGetter('aiBotTransferCases/getUIFlags');
const teams = useMapGetter('teams/getTeams');
const labels = useMapGetter('labels/getLabels');

const actionOptions = computed(() => [
  { value: 'transfer', label: t('AI_AGENT.TRANSFER.ACTIONS.TRANSFER') },
  { value: 'notify', label: t('AI_AGENT.TRANSFER.ACTIONS.NOTIFY') },
  { value: 'tag', label: t('AI_AGENT.TRANSFER.ACTIONS.TAG') },
]);

const actionName = action =>
  actionOptions.value.find(option => option.value === action)?.label || action;

const teamOptions = computed(() => [
  { value: '', label: t('AI_AGENT.TRANSFER.FIELDS.NO_TEAM') },
  ...teams.value.map(team => ({ value: team.id, label: team.name })),
]);

const labelOptions = computed(() => [
  { value: '', label: t('AI_AGENT.TRANSFER.FIELDS.NO_LABEL') },
  ...labels.value.map(label => ({ value: label.title, label: label.title })),
]);

const teamName = id => teams.value.find(team => team.id === id)?.name;

const ACTION_ICONS = {
  transfer: 'i-lucide-arrow-right-left',
  notify: 'i-lucide-bell',
  tag: 'i-lucide-tag',
};

const keywordList = record =>
  (record.trigger_keywords || '')
    .split(',')
    .map(keyword => keyword.trim())
    .filter(Boolean);

const emptyDraft = () => ({
  id: null,
  name: '',
  keywords: [],
  action: 'transfer',
  team_id: '',
  label: '',
  notification_email: '',
  message: '',
  priority: 0,
  enabled: true,
});

const draft = ref(emptyDraft());
const caseDialogRef = ref(null);
const deleteDialogRef = ref(null);

const isEditing = computed(() => draft.value.id !== null);
const dialogTitle = computed(() =>
  isEditing.value ? t('AI_AGENT.TRANSFER.EDIT') : t('AI_AGENT.TRANSFER.ADD')
);
const isSaving = computed(
  () => uiFlags.value.isCreating || uiFlags.value.isUpdating
);

const openCreateDialog = () => {
  draft.value = emptyDraft();
  caseDialogRef.value.open();
};

const openEditDialog = record => {
  draft.value = {
    id: record.id,
    name: record.name,
    keywords: record.trigger_keywords.split(',').map(keyword => keyword.trim()),
    action: record.action,
    team_id: record.team_id || '',
    label: record.label || '',
    notification_email: record.notification_email || '',
    message: record.message || '',
    priority: record.priority,
    enabled: record.enabled,
  };
  caseDialogRef.value.open();
};

const requestDelete = record => {
  draft.value = { ...emptyDraft(), id: record.id, name: record.name };
  deleteDialogRef.value.open();
};

const toggleEnabled = async record => {
  try {
    await store.dispatch('aiBotTransferCases/update', {
      botId: props.bot.id,
      id: record.id,
      enabled: !record.enabled,
    });
  } catch (error) {
    useAlert(t('AI_AGENT.TRANSFER.ERROR'));
  }
};

const confirmDelete = async () => {
  try {
    await store.dispatch('aiBotTransferCases/delete', {
      botId: props.bot.id,
      id: draft.value.id,
    });
    useAlert(t('AI_AGENT.TRANSFER.DELETED'));
  } catch (error) {
    useAlert(t('AI_AGENT.TRANSFER.ERROR'));
  } finally {
    deleteDialogRef.value.close();
  }
};

const saveCase = async () => {
  const payload = {
    name: draft.value.name,
    trigger_keywords: draft.value.keywords.join(','),
    action: draft.value.action,
    team_id: draft.value.team_id === '' ? null : draft.value.team_id,
    label: draft.value.label === '' ? null : draft.value.label,
    notification_email: draft.value.notification_email || null,
    message: draft.value.message || null,
    priority: Number(draft.value.priority) || 0,
    enabled: draft.value.enabled,
  };
  try {
    if (isEditing.value) {
      await store.dispatch('aiBotTransferCases/update', {
        botId: props.bot.id,
        id: draft.value.id,
        ...payload,
      });
      useAlert(t('AI_AGENT.TRANSFER.UPDATED'));
    } else {
      await store.dispatch('aiBotTransferCases/create', {
        botId: props.bot.id,
        ...payload,
      });
      useAlert(t('AI_AGENT.TRANSFER.CREATED'));
    }
    caseDialogRef.value.close();
  } catch (error) {
    useAlert(t('AI_AGENT.TRANSFER.ERROR'));
  }
};

onMounted(() => {
  store.dispatch('aiBotTransferCases/get', { botId: props.bot.id });
  store.dispatch('teams/get');
  store.dispatch('labels/get');
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <StudioSection
      :title="$t('AI_AGENT.TRANSFER.CUSTOM_TITLE')"
      :description="$t('AI_AGENT.TRANSFER.CUSTOM_HELP')"
      icon="i-lucide-list-plus"
      collapsible
    >
      <template #actions>
        <!-- .stop: the section header toggles the card on click. -->
        <span @click.stop>
          <Button
            icon="i-lucide-plus"
            sm
            :label="$t('AI_AGENT.TRANSFER.ADD')"
            @click="openCreateDialog"
          />
        </span>
      </template>

      <StudioEmptyState
        v-if="!records.length && !uiFlags.isFetching"
        icon="i-lucide-arrow-right-left"
        :title="$t('AI_AGENT.TRANSFER.TABLE.NO_RECORDS')"
        :description="$t('AI_AGENT.TRANSFER.EMPTY_HELP')"
      >
        <Button
          icon="i-lucide-plus"
          sm
          :label="$t('AI_AGENT.TRANSFER.ADD')"
          @click="openCreateDialog"
        />
      </StudioEmptyState>

      <ul v-else class="flex flex-col gap-2">
        <li
          v-for="record in records"
          :key="record.id"
          class="flex flex-col gap-3 p-3 transition-colors rounded-xl bg-n-alpha-1 outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-strong md:flex-row md:items-center motion-reduce:transition-none"
          :class="{ 'opacity-60': !record.enabled }"
        >
          <span
            class="flex items-center justify-center rounded-lg shrink-0 size-9 bg-n-brand/10 text-n-blue-11"
          >
            <span
              :class="ACTION_ICONS[record.action] || ACTION_ICONS.transfer"
              class="size-4"
            />
          </span>
          <div class="flex flex-col flex-1 min-w-0 gap-1.5">
            <div class="flex flex-wrap items-center gap-2">
              <span class="truncate text-heading-3 text-n-slate-12">
                {{ record.name }}
              </span>
              <StudioBadge tone="blue">
                {{ actionName(record.action) }}
              </StudioBadge>
              <StudioBadge v-if="teamName(record.team_id)">
                <span class="i-lucide-users size-3" />
                {{ teamName(record.team_id) }}
              </StudioBadge>
              <StudioBadge v-if="record.label">
                <span class="i-lucide-tag size-3" />
                {{ record.label }}
              </StudioBadge>
              <StudioBadge>
                {{
                  $t('AI_AGENT.TRANSFER.PRIORITY_BADGE', {
                    n: record.priority,
                  })
                }}
              </StudioBadge>
            </div>
            <div class="flex flex-wrap gap-1">
              <code
                v-for="keyword in keywordList(record)"
                :key="keyword"
                class="px-1.5 py-0.5 rounded bg-n-alpha-2 text-label-small text-n-slate-11"
              >
                {{ keyword }}
              </code>
            </div>
          </div>
          <div class="flex items-center gap-1 shrink-0">
            <Switch
              :model-value="record.enabled"
              class="mr-2"
              @change="toggleEnabled(record)"
            />
            <Button
              v-tooltip="$t('AI_AGENT.TRANSFER.EDIT')"
              icon="i-lucide-pencil"
              slate
              ghost
              xs
              @click="openEditDialog(record)"
            />
            <Button
              v-tooltip="$t('AI_AGENT.TRANSFER.DELETE_CONFIRM')"
              icon="i-woot-bin"
              slate
              ghost
              xs
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              @click="requestDelete(record)"
            />
          </div>
        </li>
      </ul>
    </StudioSection>

    <Dialog
      ref="caseDialogRef"
      type="edit"
      :title="dialogTitle"
      :confirm-button-label="$t('AI_AGENT.TRANSFER.SAVE')"
      :is-loading="isSaving"
      @confirm="saveCase"
    >
      <div class="flex flex-col gap-4">
        <Input
          v-model="draft.name"
          :label="$t('AI_AGENT.TRANSFER.FIELDS.NAME')"
        />
        <div class="flex flex-col gap-1">
          <TagInput
            v-model="draft.keywords"
            :placeholder="$t('AI_AGENT.TRANSFER.FIELDS.KEYWORDS')"
          />
          <p class="text-label-small text-n-slate-10">
            {{ $t('AI_AGENT.TRANSFER.FIELDS.KEYWORDS_HELP') }}
          </p>
        </div>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div class="flex flex-col gap-1.5">
            <span class="text-label text-n-slate-12">
              {{ $t('AI_AGENT.TRANSFER.FIELDS.ACTION') }}
            </span>
            <Select v-model="draft.action" :options="actionOptions" />
          </div>
          <Input
            v-model="draft.priority"
            type="number"
            :label="$t('AI_AGENT.TRANSFER.FIELDS.PRIORITY')"
          />
          <div class="flex flex-col gap-1.5">
            <span class="text-label text-n-slate-12">
              {{ $t('AI_AGENT.TRANSFER.FIELDS.TEAM') }}
            </span>
            <Select v-model="draft.team_id" :options="teamOptions" />
          </div>
          <div class="flex flex-col gap-1.5">
            <span class="text-label text-n-slate-12">
              {{ $t('AI_AGENT.TRANSFER.FIELDS.LABEL') }}
            </span>
            <Select v-model="draft.label" :options="labelOptions" />
          </div>
        </div>
        <Input
          v-model="draft.notification_email"
          :label="$t('AI_AGENT.TRANSFER.FIELDS.EMAIL')"
        />
        <Textarea
          v-model="draft.message"
          :label="$t('AI_AGENT.TRANSFER.FIELDS.MESSAGE')"
        />
        <div class="flex items-center gap-2">
          <Switch v-model="draft.enabled" />
          <span class="text-label text-n-slate-12">
            {{ $t('AI_AGENT.TRANSFER.FIELDS.ENABLED') }}
          </span>
        </div>
      </div>
    </Dialog>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="$t('AI_AGENT.TRANSFER.DELETE_TITLE')"
      :description="$t('AI_AGENT.TRANSFER.DELETE_DESCRIPTION')"
      :confirm-button-label="$t('AI_AGENT.TRANSFER.DELETE_CONFIRM')"
      :is-loading="uiFlags.isDeleting"
      @confirm="confirmDelete"
    />
  </div>
</template>
